import { MouseEventHandler, useEffect, useState } from 'react';
import classNames from 'classnames';

import { Icon, IconProps } from '../../components/icon';
import { Tooltip } from '../../components/tooltip';

import { RollbackControls } from './rollback-controls';

import styles from './pipeline.module.scss';

const RollbackDragType = 'application/x-sup-rollback';
interface SeparatorProps {
  index: number;
  addable?: boolean;
  onAdd?: MouseEventHandler;
  rollbackable?: boolean;
  onRollback?: MouseEventHandler;
  onResetRollback?: MouseEventHandler;
  onRollbackMove?: (index: number) => void;
  activeRollback?: boolean;
  icon?: IconProps['name'];
  size?: number;
}

export const Separator = ({
  index,
  addable = false,
  onAdd,
  rollbackable = false,
  onRollback,
  onResetRollback,
  onRollbackMove,
  activeRollback = false,
  icon,
  size,
}: SeparatorProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Reset drag state if activeRollback changes (e.g. moved to another index)
  useEffect(() => {
    if (!activeRollback) {
      setIsHovered(false);
    }
  }, [activeRollback]);

  const handleDragStart = (e: React.DragEvent) => {
    setIsHovered(false); // Clear hover state immediately on drag start
    e.dataTransfer.setData(RollbackDragType, index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsHovered(false);
  };

  const handleDragBase = (e: React.DragEvent) => {
    if ((rollbackable || activeRollback) && e.dataTransfer.types.includes(RollbackDragType)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      return true;
    }
    return false;
  };

  const handleDragEnter = (e: React.DragEvent) => {
    if (handleDragBase(e)) {
      setIsDragOver(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    handleDragBase(e);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only set to false if we are actually leaving the separator, not just entering a child
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(RollbackDragType)) {
      e.preventDefault();
      setIsDragOver(false);
      setIsHovered(true); // set hover state on drop
      onRollbackMove?.(index);
    }
  };

  return (
    <div
      className={classNames(styles.separator, {
        [styles.addable]: addable,
        [styles.activeRollback]: activeRollback,
        [styles.dropTarget]: isDragOver && !activeRollback, // Don't highlight the source
        [styles.hovered]: isHovered,
      })}
      onMouseEnter={(e) => {
        // Prevent hover state while dragging (buttons pressed)
        if (e.buttons === 0) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(event) => {
        // Only trigger add if not clicking on specific buttons (handled by stopPropagation there)
        addable && onAdd && onAdd(event);
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      <div className={styles.separatorContent}>
        <Icon
          name={icon ?? (addable ? 'DownArrow' : 'DownArrowOutlined')}
          size={size ?? 14}
          className={styles.arrowIcon}
        />
        {activeRollback && (
          <RollbackControls
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onReset={onResetRollback}
          />
        )}
        <div className={styles.hoverActions}>
          {rollbackable && (
            <Tooltip
              content={
                <>
                  <strong>Set rollback point</strong>
                  <br />
                  Temporarily disable all steps below this point
                </>
              }>
              <button
                type="button"
                className={styles.rollbackButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onRollback?.(e);
                }}>
                <Icon name="GitCommit" size={10} aria-hidden />
              </button>
            </Tooltip>
          )}
          <Tooltip content="Add step">
            <div>
              <Icon name="CirclePlus" size={16} className={styles.circlePlusIcon} />
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
